import {SiteLink as RouterLink} from "nav/SiteLink";
import {useLocale} from "i18n";
import {ContentSurface, Prose} from "components/ContentPage";
import {useSupportAddress} from "support/useSupportAddress";
import {contactCopy} from "./copy/contact";

/**
 * Public, crawlable **Contact & Support** page body (`/contact`, issue ui#133).
 *
 * v1 is email-only (no contact form — deferred to a follow-up issue). It doubles as a credibility
 * signal for Google Ads, so the copy is warm and human: a real support inbox, a soft response-time
 * promise, and a nudge to the FAQ/pricing for readers who can self-serve faster.
 *
 * Dumb view: every string of copy comes from {@link ./copy/contact.ts} (every locale), and the support
 * address from {@link useSupportAddress}. The copy is selected at runtime by
 * `useLocale()` — the same locale mechanism the rest of the site uses, so the `/ro` SSG tree renders
 * Romanian for free. The component only lays the copy out and wires the two links — a `mailto:` for
 * the support address and unprefixed `react-router` links to the internal FAQ/pricing anchors (the
 * `/ro` tree keeps clean paths, per `routes.tsx`). SSG-safe: token-styled markup via `<Prose>`, no
 * browser APIs, no portals, no hardcoded colors.
 */
export function ContactContent() {
	const {locale} = useLocale();
	const copy = contactCopy[locale];
	return (
		<ContentSurface>
			<Prose>
				<h1>{copy.heading}</h1>
				<ContactBody/>
			</Prose>
		</ContentSurface>
	);
}

/**
 * Route-neutral contact copy shared by the in-flow page and the Resources dialog.
 *
 * The address is the storefront's own inbox (kitecorp/transferify-ui#2264). The document's locale is
 * the fallback, so the pre-render names the inbox of the market each locale tree is published in, and
 * the page's storefront subfolder or the host's own brand takes over after mount; see
 * `support/supportAddress.ts`.
 */
export function ContactBody() {
	const {locale, canonicalLocale} = useLocale();
	const copy = contactCopy[locale];
	const supportAddress = useSupportAddress(canonicalLocale);
	return (
		<>
				<p>{copy.lede}</p>

				<h2>{copy.emailHeading}</h2>
				<p>
					{copy.emailLine.before}
					<a href={`mailto:${supportAddress}`}>{supportAddress}</a>
					{copy.emailLine.after}
				</p>
				<p>{copy.responseTime}</p>

				<h2>{copy.fasterHeading}</h2>
				<p>{copy.fasterIntro}</p>
				<ul>
					<li>
						<RouterLink to="/features#features-faq-heading">{copy.faqLink.label}</RouterLink>
						{copy.faqLink.description}
					</li>
					<li>
						<RouterLink to="/pricing">{copy.pricingLink.label}</RouterLink>
						{copy.pricingLink.description}
					</li>
				</ul>
				<p>{copy.closing}</p>
		</>
	);
}
